import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../services/api';
import { fetchSystemDayYmd } from '../utils/recentDealsDate';
import './DailyDealBlotter.css';

const DEAL_TYPE_LABELS = {
  TRANSACTION: 'Transaction',
  MONEY_MARKET: 'Money Market',
  GSEC: 'GSec',
  TBILL: 'T-Bill',
  BUYBACK: 'Buyback',
  FIXED_DEPOSIT: 'Fixed Deposit',
  REPO: 'Repo'
};

const DEAL_TYPE_BADGE_CLASS = {
  BUYBACK: 'ddb-type-badge--buyback',
  REPO: 'ddb-type-badge--repo',
  GSEC: 'ddb-type-badge--gsec',
  TBILL: 'ddb-type-badge--tbill',
  MONEY_MARKET: 'ddb-type-badge--money_market'
};

const STATUS_BADGE_CLASS = {
  Approved: 'ddb-status-badge--approved',
  'Final Approved': 'ddb-status-badge--approved',
  Rejected: 'ddb-status-badge--rejected',
  Draft: 'ddb-status-badge--draft',
  'Pending Front Office': 'ddb-status-badge--pending-front',
  'Pending Back Office Verification': 'ddb-status-badge--pending-verify',
  'Pending Final Approval': 'ddb-status-badge--pending-final'
};

const StatusBadge = ({ status, detail }) => {
  const cls = STATUS_BADGE_CLASS[status] || 'ddb-status-badge--default';
  return <span className={`ddb-status-badge ${cls}`}>{detail || status}</span>;
};

const DealTypeBadge = ({ dealType }) => {
  const label = DEAL_TYPE_LABELS[dealType] || dealType;
  const cls = DEAL_TYPE_BADGE_CLASS[dealType] || '';
  return <span className={`ddb-type-badge ${cls}`.trim()}>{label}</span>;
};

const DailyTransactionBlotter = () => {
  const [date, setDate] = useState('');
  const [systemDay, setSystemDay] = useState('');
  const [dealTypeFilter, setDealTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState({ totalDeals: 0, byDealType: {}, byStatus: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchBlotter = useCallback(async (selectedDate) => {
    if (!selectedDate) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/daily-transaction-blotter`, {
        params: { date: selectedDate },
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setDeals(response.data.data || []);
        setSummary(response.data.summary || { totalDeals: 0, byDealType: {}, byStatus: {} });
      } else {
        setError(response.data.error || 'Failed to fetch daily transaction blotter');
        setDeals([]);
      }
    } catch (err) {
      console.error('Error fetching daily transaction blotter:', err);
      setError(err.response?.data?.error || 'Failed to fetch daily transaction blotter');
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const day = await fetchSystemDayYmd();
      if (mounted) {
        setSystemDay(day);
        setDate(day);
        fetchBlotter(day);
      }
    })();
    return () => { mounted = false; };
  }, [fetchBlotter]);

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };

  const dealTypeOptions = useMemo(() => Object.keys(DEAL_TYPE_LABELS), []);
  const statusOptions = useMemo(() => Object.keys(STATUS_BADGE_CLASS), []);

  const filteredDeals = useMemo(() => (
    deals.filter((d) =>
      (!dealTypeFilter || d.dealType === dealTypeFilter) &&
      (!statusFilter || d.status === statusFilter)
    )
  ), [deals, dealTypeFilter, statusFilter]);

  const handleRefresh = () => fetchBlotter(date);

  return (
    <div className="daily-deal-blotter-page daily-transaction-blotter-page">
      <h2>Daily Transaction Blotter</h2>
      {systemDay && (
        <p className="ddb-system-day-banner">System day: {systemDay}</p>
      )}

      <div className="ddb-filter-card">
        <div className="ddb-filter-item">
          <label htmlFor="dtb-date">Value Date</label>
          <input
            id="dtb-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="ddb-filter-item">
          <label htmlFor="dtb-deal-type">Deal Type</label>
          <select
            id="dtb-deal-type"
            value={dealTypeFilter}
            onChange={(e) => setDealTypeFilter(e.target.value)}
          >
            <option value="">All Deal Types</option>
            {dealTypeOptions.map((dt) => (
              <option key={dt} value={dt}>{DEAL_TYPE_LABELS[dt]}</option>
            ))}
          </select>
        </div>
        <div className="ddb-filter-item">
          <label htmlFor="dtb-status">Status</label>
          <select
            id="dtb-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="ddb-filter-item ddb-filter-action">
          <label aria-hidden="true">&nbsp;</label>
          <button
            type="button"
            className="ddb-btn-primary"
            onClick={handleRefresh}
            disabled={loading || !date}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="ddb-error" role="alert">{error}</div>}

      {!loading && deals.length > 0 && (
        <div className="ddb-summary">
          <div className="ddb-summary-chip ddb-summary-chip--total">
            Total Deals: <strong>{summary.totalDeals}</strong>
          </div>
          {Object.entries(summary.byDealType || {}).map(([type, count]) => (
            <div key={type} className="ddb-summary-chip">
              {DEAL_TYPE_LABELS[type] || type}: <strong>{count}</strong>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="ddb-loading">
          <div className="ddb-loading-spinner" aria-hidden="true" />
          <div>Loading deals…</div>
        </div>
      )}

      {!loading && filteredDeals.length > 0 && (
        <div className="ddb-table-card">
          <div className="ddb-table-wrap">
            <table className="ddb-table">
              <thead>
                <tr>
                  <th>Deal Type</th>
                  <th>Deal Number</th>
                  <th className="center">Trade Date</th>
                  <th className="center">Value Date</th>
                  <th>Counterparty</th>
                  <th>Instrument</th>
                  <th className="num">Face Value</th>
                  <th className="num">Rate</th>
                  <th className="center">Status (stop)</th>
                  <th>Entered By</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeals.map((deal, idx) => (
                  <tr key={`${deal.dealType}-${deal.dealNumber}-${idx}`}>
                    <td><DealTypeBadge dealType={deal.dealType} /></td>
                    <td className="ddb-deal-number">{deal.dealNumber || '—'}</td>
                    <td className="center">{formatDate(deal.dealDate)}</td>
                    <td className="center">{formatDate(deal.valueDate)}</td>
                    <td>{deal.counterparty || '—'}</td>
                    <td className="ddb-instrument">{deal.instrument || '—'}</td>
                    <td className="num">{formatAmount(deal.amount)}</td>
                    <td className="num">
                      {deal.rate !== null && deal.rate !== undefined ? deal.rate : '—'}
                    </td>
                    <td className="center">
                      <StatusBadge status={deal.status} detail={deal.statusDetail} />
                    </td>
                    <td>{deal.enteredBy || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && filteredDeals.length === 0 && !error && date && (
        <div className="ddb-empty">
          <p>
            No deals found for value date {formatDate(date)}
            {dealTypeFilter ? ` (${DEAL_TYPE_LABELS[dealTypeFilter]})` : ''}
            {statusFilter ? ` — ${statusFilter}` : ''}.
          </p>
        </div>
      )}
    </div>
  );
};

export default DailyTransactionBlotter;
